import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ArrowLeft, ArrowRight, Check, Copy } from "lucide-react";

const EMPLOYMENT_TYPES = [
  "Full-time",
  "Part-time",
  "Contract",
  "Temporary",
  "Internship",
];

const GLOBAL_AREAS = [
  "EMEA",
  "APAC",
  "AMER",
  "LATAM",
  "DACH",
  "Nordics",
  "CEE",
  "MENA",
  "SEA",
  "ANZ",
  "Global",
];

const CURRENCIES = ["EUR", "USD", "GBP", "SEK", "NOK", "DKK"];

const inputClass =
  "w-full px-3 py-2 rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand-violet/30 dark:focus:ring-brand-violet-on-dark/30 focus:border-brand-violet dark:focus:border-brand-violet-on-dark";

const labelClass =
  "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

// API timestamps arrive as full ISO strings; <input type="date"> wants
// yyyy-mm-dd.
const toDateInput = (iso) => (iso ? iso.slice(0, 10) : "");

export default function JobPositionWizard() {
  const navigate = useNavigate();
  // With a positionId in the URL this same wizard edits that position
  // instead of creating a new one.
  const { positionId } = useParams();
  const isEditing = Boolean(positionId);
  const { employerUser, employerToken } = useAuthStore();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [createdPosition, setCreatedPosition] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [loadingPosition, setLoadingPosition] = useState(isEditing);
  // How many candidates are already in the pipeline, so the edit form can
  // warn that material changes will notify them.
  const [existingCandidates, setExistingCandidates] = useState(0);

  const [form, setForm] = useState({
    // Step 1
    title: "",
    employment_type: "Full-time",
    is_remote: false,
    location_country: "",
    location_region: "",
    location_city: "",
    location_global_area: "",
    closes_at: "",
    allow_peer_referrals: true,
    // Step 2
    salary_min: "",
    salary_max: "",
    salary_currency: "EUR",
    client_company_name: "",
    client_visible_to_candidates: false,
    process_steps: "",
    employer_message: "",
    // Step 3
    description: "",
  });

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  useEffect(() => {
    document.title = isEditing
      ? "Joborigo — Edit position"
      : "Joborigo — New position";
  }, [isEditing]);

  // Edit mode: load the position and convert API values into form values
  // (numbers to strings, ISO date to yyyy-mm-dd, steps array to lines).
  useEffect(() => {
    if (!isEditing) return;
    api
      .get(`/job-positions/${positionId}`, authConfig)
      .then(({ data }) => {
        setExistingCandidates(data.submission_count ?? 0);
        setForm({
          title: data.title ?? "",
          employment_type: data.employment_type ?? "Full-time",
          is_remote: data.is_remote ?? false,
          location_country: data.location_country ?? "",
          location_region: data.location_region ?? "",
          location_city: data.location_city ?? "",
          location_global_area: data.location_global_area ?? "",
          closes_at: toDateInput(data.closes_at),
          allow_peer_referrals: data.allow_peer_referrals ?? true,
          salary_min: data.salary_min?.toString() ?? "",
          salary_max: data.salary_max?.toString() ?? "",
          salary_currency: data.salary_currency ?? "EUR",
          client_company_name: data.client_company_name ?? "",
          client_visible_to_candidates:
            data.client_visible_to_candidates ?? false,
          process_steps: (data.process_steps ?? []).join("\n"),
          employer_message: data.employer_message ?? "",
          description: data.description ?? "",
        });
      })
      .catch(() => setError("Could not load this position."))
      .finally(() => setLoadingPosition(false));
  }, [positionId]);

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const canProceed = () => {
    if (step === 1) {
      return form.title.trim().length > 0 && form.employment_type;
    }
    return true;
  };

  // Edits send every field, with empty ones as null. Omitting a cleared
  // field would leave the old value in place, and sending "" instead of
  // null would look like a change to the backend's material-change check.
  const buildEditPayload = () => {
    const text = (v) => (v.trim() ? v.trim() : null);
    const steps = form.process_steps
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    return {
      title: form.title.trim(),
      employment_type: form.employment_type,
      is_remote: form.is_remote,
      allow_peer_referrals: form.allow_peer_referrals,
      location_country: text(form.location_country),
      location_region: text(form.location_region),
      location_city: text(form.location_city),
      location_global_area: form.location_global_area || null,
      closes_at: form.closes_at
        ? new Date(form.closes_at).toISOString()
        : null,
      salary_min: form.salary_min ? parseInt(form.salary_min) : null,
      salary_max: form.salary_max ? parseInt(form.salary_max) : null,
      salary_currency: form.salary_currency || null,
      client_company_name: text(form.client_company_name),
      client_visible_to_candidates: form.client_company_name.trim()
        ? form.client_visible_to_candidates
        : false,
      process_steps: steps.length > 0 ? steps : null,
      employer_message: text(form.employer_message),
      description: text(form.description),
    };
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Edit mode: patch the existing position and return to its page.
      if (isEditing) {
        await api.patch(
          `/job-positions/${positionId}`,
          buildEditPayload(),
          authConfig,
        );
        navigate(`/employers/positions/${positionId}`);
        return;
      }

      const payload = {
        title: form.title.trim(),
        employment_type: form.employment_type,
        is_remote: form.is_remote,
        allow_peer_referrals: form.allow_peer_referrals,
      };

      // Optional location fields
      if (form.location_country)
        payload.location_country = form.location_country;
      if (form.location_region) payload.location_region = form.location_region;
      if (form.location_city) payload.location_city = form.location_city;
      if (form.location_global_area)
        payload.location_global_area = form.location_global_area;
      if (form.closes_at)
        payload.closes_at = new Date(form.closes_at).toISOString();

      // Optional compensation
      if (form.salary_min) payload.salary_min = parseInt(form.salary_min);
      if (form.salary_max) payload.salary_max = parseInt(form.salary_max);
      if (form.salary_currency) payload.salary_currency = form.salary_currency;

      // Optional client context
      if (form.client_company_name) {
        payload.client_company_name = form.client_company_name;
        payload.client_visible_to_candidates =
          form.client_visible_to_candidates;
      }

      // Optional process steps — split lines
      if (form.process_steps.trim()) {
        payload.process_steps = form.process_steps
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
      }

      if (form.employer_message.trim()) {
        payload.employer_message = form.employer_message.trim();
      }

      if (form.description.trim()) {
        payload.description = form.description.trim();
      }

      const res = await api.post("/job-positions", payload, authConfig);
      setCreatedPosition(res.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submissionUrl = createdPosition
    ? `${window.location.origin}/submit/${createdPosition.submission_token}`
    : "";

  const copyUrl = () => {
    navigator.clipboard.writeText(submissionUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  // Where "back" leads: the position itself when editing, the list otherwise.
  const backPath = isEditing
    ? `/employers/positions/${positionId}`
    : "/employers/positions";

  if (loadingPosition) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-brand-dark-bg">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface">
        <Logo className="h-10 sm:h-12 flex-shrink-0" />
        <div className="flex items-center gap-4 text-sm">
          <ThemeToggle />
          <span className="hidden sm:inline-block text-gray-600 dark:text-gray-300">
            {employerUser?.company_name}
          </span>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate(backPath)}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {isEditing ? "Back to position" : "Back to positions"}
        </button>

        {step < 4 && (
          <>
            <h1 className="text-3xl font-bold mb-2">
              {isEditing ? "Edit position" : "New position"}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
              {step === 1 && "Step 1 of 3 — Basics"}
              {step === 2 && "Step 2 of 3 — Details (optional)"}
              {step === 3 && "Step 3 of 3 — Description (optional)"}
            </p>

            {/* Candidates already in the pipeline see material edits */}
            {isEditing && existingCandidates > 0 && (
              <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-700/30 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {existingCandidates} candidate
                  {existingCandidates !== 1 ? "s are" : " is"} already in this
                  pipeline. They will be notified if you change the title,
                  salary, location, remote setting or hiring process. Other
                  edits are saved silently.
                </p>
              </div>
            )}

            {/* Progress dots */}
            <div className="flex gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full ${
                    s <= step
                      ? "bg-brand-violet"
                      : "bg-gray-200 dark:bg-white/10"
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* STEP 1 — BASICS */}
        {step === 1 && (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 flex flex-col gap-5">
            <div>
              <label className={labelClass}>Position title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="e.g. Senior Backend Engineer"
                className={inputClass}
                autoFocus
              />
            </div>

            <div>
              <label className={labelClass}>Employment type *</label>
              <select
                value={form.employment_type}
                onChange={(e) => update("employment_type", e.target.value)}
                className={inputClass}
              >
                {EMPLOYMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Location</label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={form.location_city}
                  onChange={(e) => update("location_city", e.target.value)}
                  placeholder="City"
                  className={inputClass}
                />
                <input
                  type="text"
                  value={form.location_region}
                  onChange={(e) => update("location_region", e.target.value)}
                  placeholder="Region/State"
                  className={inputClass}
                />
                <input
                  type="text"
                  value={form.location_country}
                  onChange={(e) => update("location_country", e.target.value)}
                  placeholder="Country"
                  className={inputClass}
                />
                <select
                  value={form.location_global_area}
                  onChange={(e) =>
                    update("location_global_area", e.target.value)
                  }
                  className={inputClass}
                >
                  <option value="">Global area (optional)</option>
                  {GLOBAL_AREAS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_remote}
                  onChange={(e) => update("is_remote", e.target.checked)}
                  className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  This role is remote
                </span>
              </label>
            </div>

            <div>
              <label className={labelClass}>Closes at (optional)</label>
              <input
                type="date"
                value={form.closes_at}
                onChange={(e) => update("closes_at", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.allow_peer_referrals}
                  onChange={(e) =>
                    update("allow_peer_referrals", e.target.checked)
                  }
                  className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Allow candidates to refer peers to this position
                </span>
              </label>
              <p className="text-xs text-gray-400 mt-1 ml-6">
                Candidates who submit can share a referral link with peers who
                might also be a fit.
              </p>
            </div>
          </div>
        )}

        {/* STEP 2 — DETAILS */}
        {step === 2 && (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 flex flex-col gap-5">
            <div>
              <label className={labelClass}>Salary range (optional)</label>
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="number"
                  value={form.salary_min}
                  onChange={(e) => update("salary_min", e.target.value)}
                  placeholder="Min"
                  className={inputClass}
                />
                <input
                  type="number"
                  value={form.salary_max}
                  onChange={(e) => update("salary_max", e.target.value)}
                  placeholder="Max"
                  className={inputClass}
                />
                <select
                  value={form.salary_currency}
                  onChange={(e) => update("salary_currency", e.target.value)}
                  className={inputClass}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {employerUser?.is_agency && (
              <div>
                <label className={labelClass}>
                  Client company name (optional)
                </label>
                <input
                  type="text"
                  value={form.client_company_name}
                  onChange={(e) =>
                    update("client_company_name", e.target.value)
                  }
                  placeholder="e.g. Acme Corp"
                  className={inputClass}
                />
                {form.client_company_name && (
                  <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.client_visible_to_candidates}
                      onChange={(e) =>
                        update("client_visible_to_candidates", e.target.checked)
                      }
                      className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Show client name to candidates
                    </span>
                  </label>
                )}
                <p className="text-xs text-gray-400 mt-1 ml-6">
                  Hidden by default — protects your client relationship until
                  you choose to reveal.
                </p>
              </div>
            )}

            <div>
              <label className={labelClass}>
                Process steps (optional, one per line)
              </label>
              <textarea
                value={form.process_steps}
                onChange={(e) => update("process_steps", e.target.value)}
                placeholder={"Phone screen\nTechnical interview\nOnsite\nOffer"}
                className={inputClass}
                rows={4}
              />
              <p className="text-xs text-gray-400 mt-1">
                These appear on the candidate's application as the expected
                pipeline.
              </p>
            </div>

            <div>
              <label className={labelClass}>
                Default message to candidates (optional)
              </label>
              <textarea
                value={form.employer_message}
                onChange={(e) => update("employer_message", e.target.value)}
                placeholder="Thanks for your interest. We'll review and get back within 5 business days."
                className={inputClass}
                rows={3}
              />
            </div>
          </div>
        )}

        {/* STEP 3 — DESCRIPTION */}
        {step === 3 && (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 flex flex-col gap-3">
            <div>
              <label className={labelClass}>Description (optional)</label>
              <p className="text-xs text-gray-400 mb-2">
                Markdown supported. This is the value pitch your candidates see.
              </p>
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder={
                  "## About the role\n\nDescribe the opportunity, responsibilities, and what success looks like.\n\n## What we're looking for\n\nList the skills, experience, and qualities that matter."
                }
                className={`${inputClass} font-mono`}
                rows={16}
              />
            </div>
          </div>
        )}

        {/* CONFIRMATION (step 4, create mode only) */}
        {step === 4 && createdPosition && (
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-8 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Position created</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Share this link with candidates to start collecting submissions.
            </p>

            <div className="bg-gray-50 dark:bg-brand-dark-bg rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={submissionUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm font-mono text-brand-violet dark:text-brand-violet-on-dark break-all"
                />
                <button
                  onClick={copyUrl}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 transition"
                >
                  {copiedUrl ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate("/employers/positions")}
                className="px-4 py-2 border border-gray-200 dark:border-white/10 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition"
              >
                Back to positions
              </button>
            </div>
          </div>
        )}

        {/* Navigation between steps */}
        {step < 4 && (
          <div className="flex justify-between mt-6">
            <button
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex items-center gap-3">
              {/* Editing usually means changing one field, so saving is
                  available from every step, not just the last. */}
              {isEditing && step < 3 && (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !form.title.trim()}
                  className="px-4 py-2 border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark text-sm rounded-md hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 disabled:opacity-50 transition"
                >
                  {submitting ? "Saving..." : "Save changes"}
                </button>
              )}

              {step < 3 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 disabled:opacity-50 transition"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !form.title.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-violet text-white text-sm rounded-md hover:opacity-90 disabled:opacity-50 transition"
                >
                  {isEditing
                    ? submitting
                      ? "Saving..."
                      : "Save changes"
                    : submitting
                      ? "Creating..."
                      : "Create position"}
                </button>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
      </main>
    </div>
  );
}
